import { Button, Container, FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { useProgress } from '@/contexts/ProgressProvider';
import VnModalBody from '@/modals/components/VnModalBody';
import VnModalFooter from '@/modals/components/VnModalFooter';

import FuelAutoComplete from '../../components/fuel-autocomplete';
import type { AccessibleOU } from '../../models/FuelUserAccess';
import type { OuForm } from '../../store/forms/Admin/OuForm';
import { fuelUserAccessSliceSelector } from '../../store/slices/fuelUserAccess';
import { useModalContext } from '../VNModalProvider';

interface IPros {
  form: OuForm;
  onSave: (ouForm: OuForm) => void;
}

const CreateOuModal = ({ form, onSave }: IPros) => {
  const { permissions, fuelUserAccessData, accessibleOUs } = useSelector(fuelUserAccessSliceSelector);
  const [selectedOu, setSelectedOu] = useState<AccessibleOU | string>('');
  const [enableSeConnectionInfo, setEnableSeConnectionInfo] = useState(false);
  const { loading } = useProgress();
  const { onClose } = useModalContext();

  useEffect(() => {
    // console.log('CreateOuModal form changed', { form, accessibleOUs });
    if (form && form.parentId && accessibleOUs && accessibleOUs.length > 0) {
      const found = _.find(accessibleOUs, (x) => x.ouId === form.parentId);
      if (found) {
        setSelectedOu(found);
      }
    }
  }, [form]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OuForm>({
    defaultValues: useMemo(() => {
      return form;
    }, [form]),
  });

  const OnToggleButtonChange = (e, fieldName) => {
    // console.log('OnToggleButtonChange', { e });
    const value = e.target.checked == true;
    // setValue(fieldName, value);
    form.enableSeConnectionInfo = value;
    setEnableSeConnectionInfo(value);
  };

  const handleClose = () => {
    onClose();
  };

  const submit: SubmitHandler<OuForm> = async (data: OuForm) => {
    // console.log('submit', { data });
    if (enableSeConnectionInfo) {
      data.enableSeConnectionInfo = true;
    } else {
      data.enableSeConnectionInfo = false;
    }
    onSave(data);
  };

  const handleSwitchOu = async (value: AccessibleOU) => {
    // console.log('handleSwitchOu', { value, form });
    form.parentId = value.ouId;
  };

  return (
    <div>
      <VnModalBody>
        <Container className="no-padding" section="component" maxWidth="lg">
          <Grid container className="section-box" spacing={2}>
            <Grid item xs={12}>
              <FuelAutoComplete
                id="timezones-select"
                items={accessibleOUs}
                itemText="ouName"
                itemKey="ouId"
                labelText="Parent OU *"
                placeHolderText="Select Parent OU"
                multiSelect={false}
                onChange={handleSwitchOu}
                defaultValue={selectedOu}
              />
              {/* <TextField
                    variant="outlined"
                    required
                    label="Parent Id"
                    {...register('parentId')}
                    disabled
                    fullWidth
                    size="small"
                  /> */}
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                required
                label="Ou Name"
                fullWidth
                size="small"
                error={!!errors.ouName}
                placeholder="Enter Ou Name"
                {...register('ouName', { required: true })}
              />
              <Typography variant="inherit" className="text-danger">
                {errors.ouName && <span>OuName is required</span>}
              </Typography>
            </Grid>
            <Grid item xs={12} className="toggle-button-grid-row">
              <FormControlLabel
                control={
                  <Switch
                    className="fuel-toggle"
                    sx={{ marginTop: `-1px`, marginLeft: `5px` }}
                    inputProps={{ 'aria-label': '' }}
                    checked={enableSeConnectionInfo}
                    onChange={(e) => OnToggleButtonChange(e, 'enableSeConnectionInfo')}
                    size="small"
                  />
                }
                label="Enable SE Connection Info"
                labelPlacement="start"
              />
            </Grid>
            {enableSeConnectionInfo && (
              <>
                <Grid item xs={6}>
                  <TextField
                    variant="outlined"
                    label="Base Api URL"
                    {...register('seConnectionInfo.baseApiUrl')}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    variant="outlined"
                    label="Base Content Path"
                    {...register('seConnectionInfo.baseContentPath')}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Container>
      </VnModalBody>
      <VnModalFooter
        right={
          <>
            {' '}
            <Button color="success" variant="contained" disabled={loading} autoFocus onClick={handleSubmit(submit)}>
              Create OU
            </Button>
            <Button autoFocus variant="contained" className="cancel-button" onClick={handleClose}>
              Cancel
            </Button>
          </>
        }
      />
    </div>
  );
};

export default CreateOuModal;
